import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import {
  addCertifyingBody,
  CertifyingBodyCells,
  deleteCertifyingBody,
  editCertifyingBody,
  getCertifyingBodies,
} from "framework/certifyingBody";
import { Feature } from "models/api";
import { ICertifyingBody, ICertifyingBodyInput } from "models/certifyingBody";
import React, { useState } from "react";
import { useMutation } from "react-query";
import AddCertifyingBody from "./partials/AddCertifyingBody/AddCertifyingBody";
import EditCertifyingBody from "./partials/EditCertifyingBody/EditCertifyingBody";
import DeleteCertifyingBody from "./partials/DeleteCertifyingBody/DeleteCertifyingBody";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const CertifyingBodies: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterCertifyingBodies, setFilterCertifyingBodies] = useState<ICertifyingBody[]>([] as ICertifyingBody[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCertifyingBody, setSelectedCertifyingBody] = useState<ICertifyingBody>({} as ICertifyingBody);
  const [inputObj, setInputObj] = useState<ICertifyingBodyInput>({} as ICertifyingBodyInput);
  const { setAlert } = useAlert();

  const addCertifyingBodyMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addCertifyingBody(createInput);
    },
  });

  const editCertifyingBodyMutation = useMutation({
    mutationFn: (data: { createInput: FormData; id: string }) => {
      return editCertifyingBody(data.createInput, data.id);
    },
  });

  const deleteCertifyingBodyMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCertifyingBody(id);
    },
  });

  const {
    loading,
    data: CertifyingBodies,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCertifyingBodies(),
    fetchPermission: () => getPermission(65),
    section: "CertifyingBodies",
    onSuccess: (data) => setFilterCertifyingBodies(data),
    processResponse: (data) =>
      data.map((item: ICertifyingBody) => {
        return { ...item, id: item.id };
      }),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name == 'isActive' && value == null) {
      value = true;
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => { setInputObj({} as ICertifyingBodyInput); setOpenAdd(false); }

  const handleOpenEdit = (certifyingBody: ICertifyingBody) => {
    setSelectedCertifyingBody(certifyingBody);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setInputObj({} as ICertifyingBodyInput)
    setSelectedCertifyingBody({} as ICertifyingBody);
  };

  const handleOpenDelete = (certifyingBody: ICertifyingBody) => {
    setSelectedCertifyingBody(certifyingBody);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCertifyingBody({} as ICertifyingBody);
  };

  const getBodyCells = (row: ICertifyingBody) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "isActive")
        return {
          id: "isActive",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.isActive ? 'Yes' : "No"}</Typography>
            </TableCell>
          ),
        };

      if (key === "createdOn")
        return {
          id: "createdOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {new Date(row.createdOn).toLocaleDateString()}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name)
      return setAlert({
        open: true,
        message: "Please fill all required fields",
        type: "error",
      });
    try {
      setLoading2(true);

      const createInput = new FormData();
      createInput.append("Name", inputObj.name);
      createInput.append("IsActive", inputObj.isActive?.toString() || "true");
      if (inputObj.Logo) {
        const logoFile = Array.isArray(inputObj.Logo) ? inputObj.Logo[0] : inputObj.Logo;
        if (logoFile) {
          createInput.append("Logo", logoFile);
        }
      }

      await addCertifyingBodyMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Certifying body added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as ICertifyingBodyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      
      const createInput = new FormData();
      createInput.append("Name", inputObj.name || selectedCertifyingBody.name);
      createInput.append("IsActive", (inputObj.isActive !== undefined ? inputObj.isActive : selectedCertifyingBody.isActive).toString());
      if (inputObj.Logo) {
        const logoFile = Array.isArray(inputObj.Logo) ? inputObj.Logo[0] : inputObj.Logo;
        if (logoFile) {
          createInput.append("Logo", logoFile);
        }
      }

      await editCertifyingBodyMutation.mutateAsync({
        createInput,
        id: selectedCertifyingBody.id,
      });
      setAlert({
        open: true,
        message: "Certifying body updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ICertifyingBodyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCertifyingBodyMutation.mutateAsync(selectedCertifyingBody.id);
      setAlert({
        open: true,
        message: "Certifying body deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCertifyingBodies(CertifyingBodies!);
    let filtered = CertifyingBodies!.filter((certifyingBody) =>
      certifyingBody.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterCertifyingBodies(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography variant="h3" color={"header.primary"}>
              {t(feature.name)}
            </Typography>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add")}>
                <IconButton
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={t("T_search")} />
            </Stack>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCertifyingBodies.length > 0 && (
          <List
            headCells={CertifyingBodyCells}
            data={FilterCertifyingBodies}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCertifyingBodies || FilterCertifyingBodies.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_certifying_bodies")}
              message={t("T_no_certifying_bodies")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCertifyingBody
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCertifyingBody
          open={openEdit}
          loading={loading2}
          certifyingBody={selectedCertifyingBody}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCertifyingBody
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CertifyingBodies;

